import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import LOG from "./content/log";
import intakeaiImg from "./assets/intakeai.png";
import {
  CharacterRain,
  ScrambleText,
  TextScramble,
} from "./components/ui/modern-animated-hero-section";

/* ==========================================================================
   Data
   ========================================================================== */

const STACK = [
  { domain: "Languages", items: "Python · R · C++ · JavaScript · SQL" },
  {
    domain: "AI / ML",
    items: "PyTorch · TensorFlow · Transformers · LoRA / qLoRA · RAG · MLOps",
  },
  {
    domain: "Data",
    items: "Apache Spark · PySpark · Databricks · MLflow · ETL · Data Mining",
  },
  { domain: "Cloud / Infra", items: "Azure · AWS · Kubernetes · Docker" },
  { domain: "Web", items: "React · Angular · Node.js · Flask · Three.js" },
  {
    domain: "Design",
    items: "Photography · Photoshop · Illustrator · After Effects · Blender",
  },
];

const EXPERIENCE = [
  {
    period: "APR 2026 - NOW",
    location: "In stealth",
    title: "Co-Founder, CTO",
    company: "Stealth Startup",
    url: "https://stepphone.com/",
    points: [
      "Building something new in fitness and health. More on this soon.",
    ],
    stack: "AI · Fitness · Health",
  },
  {
    period: "2024 - NOW",
    location: "Halifax, NS",
    title: "Data Scientist",
    company: "Nova Scotia Health",
    points: [
      "Designed a production LLM application over localized healthcare data: ReAct prompting, LoRA / qLoRA fine-tuning, chain-of-thought evaluation.",
      "Built a custom Python library bridging NeuralProphet forecasting models with MLflow for reproducible training runs.",
      "Shipped across the whole stack: Flask APIs, Node.js services, Angular frontends.",
    ],
    stack: "Python · LLMs · Databricks · Azure · Spark · Node.js",
  },
  {
    period: "JUN 2025 - MAY 2026",
    location: "Halifax, NS",
    title: "Founder",
    company: "Recipo",
    url: "https://recipo.io/",
    points: [
      "Gamified inventory-first meal planning and nutrition to create healthy habit loops.",
      "I failed at building and marketing it well enough, and the app is shelved. The lessons came with me.",
    ],
    stack: "Product · Nutrition · Habit Design",
  },
  {
    period: "AUG - NOV 2025",
    location: "San Francisco Bay Area",
    title: "Product Owner",
    company: "Lokam.ai",
    url: "https://www.lokam.ai/",
    points: [
      "Built voice agents, working across the product and engineering teams.",
      "Owned product direction and delivery: roadmap, priorities, and customer feedback loops.",
      "The startup has since raised 350K USD.",
    ],
    stack: "Product · Voice Agents · AI",
  },
  {
    period: "NOV 2021 - NOV 2022",
    location: "Hyderabad, IN",
    title: "Machine Learning Associate Researcher",
    company: "Indian Institute of Technology, Hyderabad",
    points: [
      "Applied research in data science and deep learning.",
    ],
    stack: "Data Science · Deep Learning · Python",
  },
  {
    period: "2022",
    location: "Windsor, ON",
    title: "Data Science Researcher",
    company: "MITACS",
    points: [
      "Developed a Transformer-based algorithm improving glaucoma prediction accuracy.",
      "Researched a semantic-segmentation framework in PyTorch and OpenCV for telemedicine imaging.",
      "Built ETL pipelines with SQL on AWS Glue.",
    ],
    stack: "PyTorch · OpenCV · Transformers · AWS · SQL",
  },
  {
    period: "MAY - JUL 2021",
    location: "Varanasi, IN",
    title: "Cybersecurity Research Affiliate",
    company: "Indian Institute of Technology (BHU), Varanasi",
    points: [
      "Built LiteEx, a lightweight feature extraction tool for captured network traces, later published through IEEE.",
    ],
    stack: "Cybersecurity · Data Analysis · Python",
  },
];

const PROJECTS = [
  {
    title: "WiFi Sense",
    category: "Signal Processing",
    year: "2026",
    description:
      "Detects human presence, motion, and walking gait from a Mac's WiFi radio. No cameras: the app samples signal strength 20 times a second, runs frequency analysis, and renders the result live in a 3D view with signal charts and an RF coverage map.",
    points: [
      "Three input pipelines: CoreWLAN RSSI sampler, gateway ping jitter, optional ESP32 CSI frames",
      "DSP classifier separates quiet, motion bursts, and walking rhythm in the 0.8 to 2.6 Hz band",
      "Measured signals are labeled apart from reconstructed pose",
    ],
    stack: "JavaScript · Swift · DSP · Three.js · ESP32",
    link: "https://github.com/sudhxan/SurveillanceDetection",
  },
  {
    title: "Code Crawler",
    category: "Developer Tools",
    year: "2026",
    description:
      "Estimates how much of a codebase was written by AI tools versus humans. Five weighted heuristics score every line: naming, comment style, structural uniformity, token entropy, and git signals.",
    points: [
      "Per-line AI probability, aggregated into file and repo reports",
      "Runs as a CLI or as a GitHub Action on pull requests",
    ],
    stack: "TypeScript · Node.js · GitHub Actions",
    link: "https://github.com/sudhxan/code-crawler",
    hunt: "https://www.producthunt.com/products/code-crawler?launch=code-crawler",
  },
  {
    title: "Imagina MCP",
    category: "AI Tooling",
    year: "2026",
    description:
      'An MCP server that downloads company logos from a plain prompt in the IDE, like "download the Shopify logo". No API keys: a curated database of 200+ companies plus live search resolves the domain, then four cascading sources fetch the image.',
    points: [
      "Fuzzy matching handles typos and abbreviations",
      "Magic byte validation rejects error pages and broken images",
      "Bulk mode downloads up to 20 logos in parallel",
    ],
    stack: "TypeScript · MCP · Node.js",
    link: "https://github.com/sudhxan/Imagina-MCP",
  },
  {
    title: "IntakeAI",
    category: "Applied AI",
    year: "2026",
    description:
      "A voice AI agent that centralizes 811 calls for family-doctor waitlists, built so the same intake mechanism extends to any healthcare queue. The agent works a built-in form, asking each question and capturing the answer; once intake is complete, a simulation ranks the case by priority against rules the admin sets. The goal is to give Canadians transparency into where they stand.",
    points: [
      "Voice agent drives a built-in form: it asks the questions and records the answers",
      "Admin-defined rules feed a simulation that assigns priority once intake finishes",
      "Intake mechanism generalizes past 811 to other healthcare waitlists",
    ],
    stack: "Voice AI · TypeScript",
    link: "https://github.com/sudhxan/IntakeAI",
    live: "https://intake-ai-gamma.vercel.app/",
    image: intakeaiImg,
  },
];

const RECOGNITION = [
  {
    year: "2026",
    title: "Speaker, e-Health 2026 Conference",
    note: "Presented FFAST — natural-language health data analytics, Nova Scotia Health",
    link: "https://www.e-healthconference.com/blog/balancing-access-and-accountability-in-a-data-driven-health-system/",
  },
  {
    year: "2023",
    title: "MITACS Globalink Graduate Fellowship",
    note: "Master's research scholarship",
  },
  {
    year: "2023",
    title: "IEEE Publication: LiteEx GUI",
    note: "Published research paper",
  },
  {
    year: "2022",
    title: "Max Planck Institute for Informatics",
    note: "Research fellow select, computer networking",
  },
];

const SOCIALS = [
  { label: "GitHub", handle: "@sudhxan", href: "https://github.com/sudhxan" },
  {
    label: "LinkedIn",
    handle: "hariharasudhan-g",
    href: "https://linkedin.com/in/hariharasudhan-g-6143651b8",
  },
  { label: "Medium", handle: "@sudhxan", href: "https://medium.com/@sudhxan" },
];

const EMAIL = "hariharasudhang@icloud.com";

/* ==========================================================================
   Small pieces
   ========================================================================== */

const HalifaxClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="tabular-nums">
      {now.toLocaleTimeString("en-CA", {
        hour12: false,
        timeZone: "America/Halifax",
      })}
    </span>
  );
};

const Meta = ({ label, children, className = "" }) => (
  <div className={className}>
    <p className="mb-1 text-[10px] uppercase tracking-[0.25em] text-dim">
      {label}
    </p>
    <p className="text-xs text-mut">{children}</p>
  </div>
);

const SectionHead = ({ index, title, note }) => (
  <div className="rv hairline-b mb-14 flex items-end justify-between pb-5 sm:mb-20">
    <div className="flex items-baseline gap-4 sm:gap-6">
      <span className="font-mono text-xs text-acc">[{index}/06]</span>
      <ScrambleText
        as="h2"
        text={title}
        className="display text-2xl font-semibold uppercase text-ink sm:text-4xl"
      />
    </div>
    <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-dim sm:block">
      {note}
    </span>
  </div>
);

/* Preloader: the full raining-letters treatment. A dense character rain
   with a huge title that scrambles through a boot sequence, then hands
   over to the site. Click anywhere to skip. */
const BOOT_PHRASES = [
  "Hello, friend.",
  "Hello, friend?",
  "That's lame.",
  "Maybe I should give you a name.",
  "But that's a slippery slope.",
  "You're only in my head.",
  "We have to remember that.",
];

const Preloader = ({ onDone }) => {
  const [exiting, setExiting] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onDone();
      return;
    }
    const scrambler = new TextScramble(titleRef.current, 18);
    let cancelled = false;
    let timer;
    const run = (i) => {
      if (cancelled) return;
      scrambler.setText(BOOT_PHRASES[i]).then(() => {
        const last = i === BOOT_PHRASES.length - 1;
        timer = setTimeout(
          () => (last ? setExiting(true) : run(i + 1)),
          last ? 1500 : 1900
        );
      });
    };
    run(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      scrambler.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (exiting) {
      const t = setTimeout(onDone, 500);
      return () => clearTimeout(t);
    }
  }, [exiting, onDone]);

  return (
    <div
      className={`fixed inset-0 z-[100] cursor-pointer overflow-hidden bg-bg ${
        exiting ? "preloader-exit" : ""
      }`}
      onClick={() => setExiting(true)}
      role="presentation"
    >
      <CharacterRain density={2.2} />

      <div className="absolute left-0 top-0 flex w-full justify-between p-6 font-mono text-[10px] uppercase tracking-[0.25em] text-dim sm:p-10">
        <span>Hariharasudhan Giridharan</span>
        <span>Portfolio / {new Date().getFullYear()}</span>
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
        <h1
          ref={titleRef}
          className="text-center font-mono text-2xl font-bold tracking-wider text-ink sm:text-4xl lg:text-6xl"
        >
          {" "}
        </h1>
      </div>

      <p className="absolute bottom-6 right-6 font-mono text-[10px] uppercase tracking-[0.25em] text-dim sm:bottom-10 sm:right-10">
        Click to skip
      </p>
    </div>
  );
};

/* ==========================================================================
   Navigation
   ========================================================================== */

const NAV_ITEMS = [
  ["01", "Profile", "about"],
  ["02", "Stack", "stack"],
  ["03", "Work", "work"],
  ["04", "Experiments", "projects"],
  ["05", "Log", "log"],
  ["06", "Contact", "contact"],
];

const Nav = () => {
  const [open, setOpen] = useState(false);

  const jump = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-bg/80 backdrop-blur-md">
        <nav className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="display text-sm font-semibold tracking-tight text-ink"
          >
            HG<span className="blink text-acc">_</span>
          </button>

          <div className="hidden items-center gap-8 md:flex">
            {NAV_ITEMS.map(([n, label, id]) => (
              <button
                key={id}
                onClick={() => jump(id)}
                className="group font-mono text-[11px] uppercase tracking-[0.2em] text-mut transition-colors hover:text-ink"
              >
                <span className="mr-1 text-acc/60 group-hover:text-acc">
                  {n}
                </span>
                {label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-mut md:hidden"
            aria-expanded={open}
            aria-label="Toggle menu"
          >
            {open ? "[ close ]" : "[ menu ]"}
          </button>
        </nav>
      </header>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 flex flex-col justify-center gap-2 bg-bg px-8 md:hidden">
          {NAV_ITEMS.map(([n, label, id]) => (
            <button
              key={id}
              onClick={() => jump(id)}
              className="hairline-b flex items-baseline gap-4 py-5 text-left"
            >
              <span className="font-mono text-xs text-acc">{n}</span>
              <span className="display text-3xl font-semibold uppercase text-ink">
                {label}
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

/* ==========================================================================
   Hero
   ========================================================================== */

const Hero = () => (
  <section className="relative flex min-h-screen flex-col justify-end overflow-hidden">
    <CharacterRain density={1.1} className="opacity-90" />
    {/* fade the rain into the page below */}
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-bg" />

    <div className="relative mx-auto w-full max-w-6xl px-6 pb-10 pt-32 sm:pb-14">
      <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-mut">
        Hariharasudhan Giridharan
        <span className="mx-3 text-dim">/</span>
        <span className="text-dim">Halifax, Nova Scotia</span>
      </p>

      <h1 className="display font-bold uppercase leading-[0.92] text-ink">
        <span className="block text-[15vw] sm:text-[11vw] lg:text-[9.5rem]">
          Hey
        </span>
        <span className="text-outline block text-[15vw] sm:text-[11vw] lg:text-[9.5rem]">
          Stalker
          <span className="blink text-acc" style={{ WebkitTextStroke: 0 }}>!</span>
        </span>
      </h1>

      <div className="mt-8 flex flex-col justify-between gap-8 sm:flex-row sm:items-end">
        <p className="max-w-md font-mono text-xs leading-relaxed text-mut sm:text-sm">
          I build things that think. Scroll down to learn more about my philosophy.
        </p>

        <div className="grid grid-cols-2 gap-8 border-t border-line pt-5 sm:min-w-[260px] sm:border-t-0 sm:pt-0">
          <Meta label="Local time">
            <HalifaxClock />
          </Meta>
          <Meta label="Coords">44.73°N 63.67°W</Meta>
        </div>
      </div>
    </div>
  </section>
);

const Ticker = () => {
  const line =
    "INTELLIGENT SYSTEMS /// SECURE LLM PIPELINES /// DATA ENGINEERING /// FULL-STACK /// ";
  return (
    <div className="marquee hairline-t hairline-b py-3" aria-hidden="true">
      <div className="marquee-track font-mono text-[11px] uppercase tracking-[0.35em] text-dim">
        {line}
        {line}
      </div>
    </div>
  );
};

/* ==========================================================================
   Sections
   ========================================================================== */

const About = () => (
  <section id="about" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
    <SectionHead index="01" title="Profile" note="cat about.md" />

    <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <p className="rv display text-2xl font-medium leading-snug text-ink sm:text-4xl">
          I build intelligent systems that respect the data they're trusted
          with<span className="text-acc">.</span>
        </p>
        <div className="rv rv-d1 mt-8 max-w-xl space-y-5 font-mono text-sm leading-relaxed text-mut">
          <p>
            My focus is large language models for private, secure data:
            production RAG pipelines, fine-tuning with LoRA and qLoRA, and the
            MLOps scaffolding that keeps it all honest. Currently doing that
            for healthcare at Nova Scotia Health.
          </p>
          <p>
            I work across models, pipelines, and interfaces, and hold all of
            it to one standard: it should work well and read clearly.
          </p>
          <p className="text-dim">
            Away from the screen: tennis, photography, and reading. Robin
            Sharma and Darius Foroux lately.
          </p>
        </div>

      </div>

      <div className="lg:col-span-4 lg:col-start-9">
        <div className="rv rv-d1">
          <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
            Education
          </p>
          <div className="hairline-t py-4">
            <p className="font-mono text-[10px] text-dim">EDU.01 · 2023 - 2024</p>
            <p className="mt-1 text-sm text-ink">Master of Digital Innovation, Data Science</p>
            <p className="font-mono text-xs text-mut">Dalhousie University · Canada</p>
          </div>
          <div className="hairline-t py-4">
            <p className="font-mono text-[10px] text-dim">EDU.02 · 2019 - 2023</p>
            <p className="mt-1 text-sm text-ink">B.Tech, Instrumentation &amp; Control</p>
            <p className="font-mono text-xs text-mut">NIT Tiruchirappalli · India</p>
          </div>
        </div>

        <div className="rv rv-d2 mt-12">
          <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
            Recognition
          </p>
          {RECOGNITION.map((r) => (
            <div key={r.title} className="hairline-t flex items-baseline gap-5 py-4">
              <span className="font-mono text-xs text-dim">{r.year}</span>
              <div>
                <p className="text-sm text-ink">
                  {r.link ? (
                    <a
                      href={r.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="u-link transition-colors hover:text-ink"
                    >
                      {r.title} ↗
                    </a>
                  ) : (
                    r.title
                  )}
                </p>
                <p className="font-mono text-xs text-dim">{r.note}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Stack = () => (
  <section id="stack" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
    <SectionHead index="02" title="Stack" note="ls -la /skills" />
    <div>
      {STACK.map((row, i) => (
        <div
          key={row.domain}
          className="rv hairline-t grid grid-cols-12 items-baseline gap-4 py-6 last:border-b last:border-line sm:py-7"
        >
          <span className="col-span-2 font-mono text-[11px] text-dim sm:col-span-1">
            SKL.{String(i + 1).padStart(2, "0")}
          </span>
          <h3 className="display col-span-10 text-lg font-medium text-ink sm:col-span-4 sm:text-xl">
            {row.domain}
          </h3>
          <p className="col-span-10 col-start-3 font-mono text-xs leading-relaxed text-mut sm:col-span-7 sm:col-start-6 sm:text-sm">
            {row.items}
          </p>
        </div>
      ))}
    </div>
  </section>
);

const Work = () => (
  <section id="work" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
    <SectionHead index="03" title="Work" note="git log --oneline" />
    <div className="space-y-20">
      {EXPERIENCE.map((job, i) => (
        <article key={job.company} className="rv grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] text-dim">
              EXP.{String(i + 1).padStart(2, "0")}
            </p>
            <p className="mt-2 font-mono text-xs tracking-[0.15em] text-acc">
              {job.period}
            </p>
            <p className="mt-1 font-mono text-xs text-dim">{job.location}</p>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            <h3 className="display text-2xl font-semibold text-ink sm:text-3xl">
              {job.title}
            </h3>
            <p className="mt-1 font-mono text-sm text-mut">
              {job.url ? (
                <a
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="u-link transition-colors hover:text-ink"
                >
                  {job.company} ↗
                </a>
              ) : (
                job.company
              )}
            </p>
            <ul className="mt-6 space-y-3">
              {job.points.map((p) => (
                <li key={p} className="flex gap-3 font-mono text-xs leading-relaxed text-mut sm:text-sm">
                  <span className="text-dim">{">"}</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-dim">
              {job.stack}
            </p>
          </div>
        </article>
      ))}
    </div>
  </section>
);

const Projects = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="projects" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
      <SectionHead index="04" title="Experiments" note="ls ~/experiments" />

      <p className="rv mb-14 max-w-md font-mono text-xs leading-relaxed text-mut">
        Code I like to play around with, built for myself and used daily.
      </p>

      <div className="rv">
        {PROJECTS.map((proj, i) => {
          const open = openIdx === i;
          return (
            <div key={proj.title} className="proj-row hairline-t last:border-b last:border-line">
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                aria-expanded={open}
                className="grid w-full grid-cols-12 items-baseline gap-4 py-7 text-left sm:py-9"
              >
                <span className="col-span-2 font-mono text-[11px] text-dim sm:col-span-1">
                  PRJ.{String(i + 1).padStart(2, "0")}
                </span>
                <h3 className={`proj-title display col-span-10 text-2xl font-semibold sm:col-span-6 sm:text-4xl ${open ? "text-acc" : "text-ink"}`}>
                  {proj.title}
                </h3>
                <span className="col-span-6 col-start-3 font-mono text-[11px] uppercase tracking-[0.2em] text-dim sm:col-span-3 sm:col-start-8 sm:text-right">
                  {proj.category} · {proj.year}
                </span>
                <span className={`proj-arrow col-span-2 text-right font-mono text-lg text-acc sm:col-span-2 ${open ? "!opacity-100 !transform-none" : ""}`}>
                  {open ? "×" : "↗"}
                </span>
              </button>

              <div className={`proj-detail ${open ? "open" : ""}`}>
                <div>
                  <div className="grid grid-cols-12 gap-4 pb-9">
                    <div className="col-span-12 sm:col-span-6 sm:col-start-2">
                      <p className="font-mono text-xs leading-relaxed text-mut sm:text-sm">
                        {proj.description}
                      </p>
                      {proj.points.length > 0 && (
                        <ul className="mt-5 space-y-2">
                          {proj.points.map((p) => (
                            <li key={p} className="flex gap-3 font-mono text-xs text-mut">
                              <span className="text-acc">+</span>
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {proj.image && (
                        <a
                          href={proj.live || proj.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-8 block overflow-hidden border border-line"
                        >
                          <img
                            src={proj.image}
                            alt={`${proj.title} interface`}
                            loading="lazy"
                            className="w-full grayscale transition duration-500 hover:grayscale-0"
                          />
                        </a>
                      )}
                    </div>
                    <div className="col-span-12 sm:col-span-4 sm:col-start-9">
                      <p className="font-mono text-[11px] uppercase tracking-[0.15em] leading-relaxed text-dim">
                        {proj.stack}
                      </p>
                      <div className="mt-5 flex flex-col gap-3">
                        <a
                          href={proj.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="u-link self-start font-mono text-xs uppercase tracking-[0.2em] text-ink"
                        >
                          View source ↗
                        </a>
                        {proj.live && (
                          <a
                            href={proj.live}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="u-link self-start font-mono text-xs uppercase tracking-[0.2em] text-acc"
                          >
                            Live demo ↗
                          </a>
                        )}
                        {proj.hunt && (
                          <a
                            href={proj.hunt}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="u-link self-start font-mono text-xs uppercase tracking-[0.2em] text-ink"
                          >
                            Product Hunt ↗
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rv rv-d1 mt-14 text-center">
        <a
          href="https://github.com/sudhxan"
          target="_blank"
          rel="noopener noreferrer"
          className="u-link font-mono text-xs uppercase tracking-[0.25em] text-mut hover:text-ink"
        >
          Full archive on GitHub ↗
        </a>
      </div>
    </section>
  );
};

/* ==========================================================================
   Log: learning journal, entries live in src/content/log.js
   ========================================================================== */

const Log = () => (
  <section id="log" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
    <SectionHead index="05" title="Log" note="tail -f learnings.log" />

    <p className="rv mb-14 max-w-md font-mono text-xs leading-relaxed text-mut">
      Notes to self: reading, photography, tennis, work. Updated when
      something sticks.
    </p>

    <div className="rv">
      {LOG.map((entry, i) => (
        <article
          key={entry.date + entry.title}
          className="hairline-t grid grid-cols-12 gap-4 py-8 last:border-b last:border-line sm:py-10"
        >
          <div className="col-span-12 sm:col-span-3">
            <p className="font-mono text-[11px] text-dim">
              LOG.{String(LOG.length - i).padStart(2, "0")}
            </p>
            <p className="mt-1 font-mono text-xs tracking-[0.1em] text-acc">
              {entry.date}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
              {entry.tag}
            </p>
          </div>
          <div className="col-span-12 sm:col-span-8 sm:col-start-5">
            <h3 className="display text-xl font-semibold text-ink sm:text-2xl">
              {entry.title}
            </h3>
            <p className="mt-3 max-w-xl font-mono text-xs leading-relaxed text-mut sm:text-sm">
              {entry.body}
            </p>
          </div>
        </article>
      ))}
    </div>
  </section>
);

/* ==========================================================================
   Contact
   ========================================================================== */

const ContactForm = () => {
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    emailjs
      .sendForm(
        "service_ubns96x",
        "template_50lcpzb",
        formRef.current,
        "JdxD8eqGIfxWMD6FI"
      )
      .then(
        () => {
          setResult({ ok: true, msg: "Message received. I'll reply soon." });
          formRef.current.reset();
        },
        (err) => {
          setResult({ ok: false, msg: "Delivery failed. Email me directly." });
          console.error("EmailJS error:", err);
        }
      )
      .finally(() => setSending(false));
  };

  return (
    <form ref={formRef} onSubmit={send} className="space-y-8">
      <div>
        <label htmlFor="user_name" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
          Name
        </label>
        <input id="user_name" name="user_name" type="text" required className="field" placeholder="Your name" />
      </div>
      <div>
        <label htmlFor="user_email" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
          Email
        </label>
        <input id="user_email" name="user_email" type="email" required className="field" placeholder="you@domain.com" />
      </div>
      <div>
        <label htmlFor="message" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
          Message
        </label>
        <textarea id="message" name="message" rows="4" required className="field resize-none" placeholder="Your message" />
      </div>
      <button
        type="submit"
        disabled={sending}
        className="border border-line-strong px-8 py-3 font-mono text-xs uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-acc hover:bg-acc hover:text-black disabled:opacity-40"
      >
        {sending ? "Transmitting…" : "Send →"}
      </button>
      {result && (
        <p className={`font-mono text-xs ${result.ok ? "text-acc" : "text-red-400"}`} role="status">
          {result.ok ? "[ok] " : "[fail] "}
          {result.msg}
        </p>
      )}
    </form>
  );
};

const Contact = () => (
  <section id="contact" className="mx-auto max-w-6xl scroll-mt-20 px-6 py-24 sm:py-32">
    <SectionHead index="06" title="Contact" note="ping operator" />

    <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <p className="rv display text-3xl font-medium leading-snug text-ink sm:text-5xl">
          Get in touch<span className="text-acc">.</span>
        </p>
        <a
          href={`mailto:${EMAIL}`}
          className="rv rv-d1 u-link mt-8 inline-block break-all font-mono text-sm text-mut transition-colors hover:text-ink sm:text-base"
        >
          {EMAIL}
        </a>

        <div className="rv rv-d2 mt-14">
          {SOCIALS.map((s, i) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hairline-t group flex items-baseline justify-between py-4"
            >
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-mut transition-colors group-hover:text-ink">
                <span className="mr-3 text-dim">LNK.{String(i + 1).padStart(2, "0")}</span>
                {s.label}
              </span>
              <span className="font-mono text-xs text-dim transition-colors group-hover:text-acc">
                {s.handle} ↗
              </span>
            </a>
          ))}
        </div>
      </div>

      <div className="rv rv-d1 lg:col-span-5 lg:col-start-8">
        <ContactForm />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="hairline-t">
    <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-10 font-mono text-[11px] text-dim sm:flex-row sm:items-center">
      <p>
        © {new Date().getFullYear()} Hariharasudhan Giridharan
        <span className="ml-3 text-dim">{"// BUILD 0x00FF6A"}</span>
      </p>
      <p className="uppercase tracking-[0.2em]">
        44.73°N 63.67°W · <HalifaxClock />
      </p>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="u-link uppercase tracking-[0.2em] text-mut hover:text-ink"
      >
        Back to top ↑
      </button>
    </div>
  </footer>
);

/* ==========================================================================
   App
   ========================================================================== */

const App = () => {
  const [loading, setLoading] = useState(true);

  // lock scroll behind the preloader
  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [loading]);

  // honor deep links (/#section) once the preloader releases scroll
  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: "instant" });
  }, [loading]);

  // scroll-reveal observer
  useEffect(() => {
    if (loading) return;
    const els = document.querySelectorAll(".rv");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [loading]);

  return (
    <div className="min-h-screen bg-bg text-ink">
      {loading && <Preloader onDone={() => setLoading(false)} />}

      {/* texture overlays */}
      <div className="grain" aria-hidden="true" />
      <div className="raster" aria-hidden="true" />

      <Nav />
      <main>
        <Hero />
        <Ticker />
        <About />
        <Stack />
        <Work />
        <Projects />
        <Log />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default App;
